import { useQuery } from '@tanstack/react-query'
import { useRouterState } from '@tanstack/react-router'
import { createContext, use, useMemo, type JSX, type ReactNode } from 'react'

import {
  ALERT_REPORTS_QUERY_KEY,
  fetchAlertReports,
  type AlertReport
} from '../lib/alert-rules-api.ts'
import type { AlertSearch } from '../lib/alert-view.ts'
import { QUIET_QUERY_META } from '../lib/query-activity.ts'

/**
 * What a report page offers the Save view button.
 *
 * The toolbar is shared by every table, including ones that are not a report.
 * It reads this and renders nothing when the value is null, which is every
 * page the alert catalogue does not list. The page publishes its period while
 * it renders, and this provider renders first, so the period is not here: the
 * button reads `pagePeriod()` itself.
 */
export interface SaveViewOffer {
  report: AlertReport
  search: AlertSearch
}

const SaveViewOfferContext = createContext<SaveViewOffer | null>(null)

/**
 * The offer on this page, or null when the page is not an alertable report.
 *
 * @returns The offer.
 */
export function useSaveViewOffer(): SaveViewOffer | null {
  return use(SaveViewOfferContext)
}

/**
 * Copy a router search into the plain record a saved view reads.
 *
 * @param search - The router's search.
 * @returns The same entries.
 */
function readSearch(search: object): AlertSearch {
  return Object.fromEntries(Object.entries(search))
}

/**
 * SaveViewOfferProvider — the report and the URL the Save view button saves.
 *
 * It sits beside Alert me, in the root layout, and matches the path the same
 * way: a report the catalogue lists can be saved, and every other page offers
 * nothing. The catalogue changes only with a deploy, so it loads once.
 *
 * @param props - Provider props.
 * @param props.children - The page.
 * @returns The provider.
 */
export function SaveViewOfferProvider({
  children
}: {
  children: ReactNode
}): JSX.Element {
  const pathname = useRouterState({ select: state => state.location.pathname })
  const search = useRouterState({ select: state => state.location.search })
  const catalogue = useQuery({
    queryKey: ALERT_REPORTS_QUERY_KEY,
    meta: QUIET_QUERY_META,
    queryFn: fetchAlertReports,
    staleTime: Infinity,
    retry: false
  })
  const report =
    catalogue.data?.reports.find(item => item.path === pathname) ?? null
  const offer = useMemo(
    () => (report ? { report, search: readSearch(search) } : null),
    [report, search]
  )

  return <SaveViewOfferContext value={offer}>{children}</SaveViewOfferContext>
}
